import React, { useMemo, useState } from "react";
import { Checkbox, Input, Popover, Radio } from "antd";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";

type DeliverableTypeGroup = {
	label: string;
	selection: "multiple" | "single";
	options: string[];
};

export const DELIVERABLE_TYPE_GROUPS: DeliverableTypeGroup[] = [
	{
		label: "Asset Type",
		selection: "multiple",
		options: [
			"HD Video Asset",
			"TikTok Video",
			"Instagram Video",
			"Facebook Video",
			"YouTube Video",
			"YouTube Shorts",
		],
	},
	{
		label: "Ad Spark Code",
		selection: "single",
		options: ["30 days", "60 days", "90 days", "360 days"],
	},
	{
		label: "Crossposting",
		selection: "multiple",
		options: [
			"Crosspost to TikTok",
			"Crosspost to Instagram",
			"Crosspost to Facebook",
			"Crosspost to YouTube",
			"Crosspost to YouTube Shorts",
		],
	},
	{
		label: "Reshare/Repost",
		selection: "multiple",
		options: [
			"Instagram Story Reshare",
			"Instagram Repost/Engagement",
			"TikTok Story Reshare",
			"TikTok Repost/Engagement",
		],
	},
	{
		label: "Others",
		selection: "multiple",
		options: [
			"Collaboration Post with brand's social handles",
			"Linking of Yellow Basket",
			"Adding Brand Mention/Link to Creator Profile Bio",
			"Local Event Attendance",
			"Overseas Event Attendance",
			"Repurposing of Creator Asset",
			"Reposting of Creator Asset on Brand Social Pages",
			"Livestream",
		],
	},
];

const SPARK_CODE_OPTIONS = new Set(
	DELIVERABLE_TYPE_GROUPS.find((group) => group.label === "Ad Spark Code")?.options || [],
);

const Trigger = styled.button<{ $open: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	width: 100%;
	height: 40px;
	padding: 0 12px;
	border: 1px solid ${(props) => (props.$open ? "#8193f1" : "#cdd4f6")};
	border-radius: 8px;
	background: #fff;
	box-shadow: ${(props) => (props.$open ? "0 0 0 2px rgba(68, 96, 239, 0.08)" : "none")};
	color: #303030;
	font: 400 14px var(--font-family-app);
	cursor: pointer;

	&:hover {
		border-color: #8193f1;
	}
`;

const TriggerValue = styled.span<{ $placeholder: boolean }>`
	min-width: 0;
	overflow: hidden;
	color: ${(props) => (props.$placeholder ? "#a0a0a0" : "#303030")};
	text-align: left;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Popup = styled.div`
	width: min(520px, calc(100vw - 32px));
	padding: 12px 0 8px;
	border: 1px solid #d8ddf8;
	border-radius: 12px;
	background: #fff;
	box-shadow: 0 4px 12px rgba(21, 89, 165, 0.23);
	font-family: var(--font-family-app);
`;

const Search = styled(Input)`
	width: calc(100% - 24px);
	height: 40px;
	margin: 0 12px 8px;
	border-color: #d8d8d8;
	border-radius: 6px;
	font-size: 14px;
`;

const Options = styled.div`
	/* Six 38px option rows plus room for the visible group heading. */
	max-height: 266px;
	overflow-y: auto;
	padding: 0 12px 8px;
`;

const Group = styled.div`
	padding-top: 12px;
`;

const GroupLabel = styled.div`
	margin-bottom: 5px;
	color: #6539e6;
	font-size: 12px;
	font-weight: 600;
`;

const Option = styled.label`
	display: flex;
	align-items: center;
	gap: 8px;
	min-height: 38px;
	color: #3b3b3b;
	font-size: 14px;
	cursor: pointer;

	.ant-checkbox-inner,
	.ant-radio-inner {
		width: 16px;
		height: 16px;
		border-color: #b8b8b8;
	}
`;

const Empty = styled.div`
	padding: 28px 12px;
	color: #8a8a8a;
	font-size: 13px;
	text-align: center;
`;

interface DeliverableTypeSelectProps {
	value: string[];
	onChange: (value: string[]) => void;
	placeholder?: string;
	"aria-label"?: string;
}

export const DeliverableTypeSelect: React.FC<DeliverableTypeSelectProps> = ({
	value,
	onChange,
	placeholder = "Select deliverable type",
	"aria-label": ariaLabel,
}) => {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const normalizedQuery = query.trim().toLowerCase();
	const visibleGroups = useMemo(
		() =>
			DELIVERABLE_TYPE_GROUPS.map((group) => ({
				...group,
				options: group.options.filter(
					(option) =>
						!normalizedQuery ||
						option.toLowerCase().includes(normalizedQuery) ||
						group.label.toLowerCase().includes(normalizedQuery),
				),
			})).filter((group) => group.options.length > 0),
		[normalizedQuery],
	);

	const toggleMultiple = (option: string) => {
		onChange(value.includes(option) ? value.filter((item) => item !== option) : [...value, option]);
	};

	const chooseSparkCode = (option: string) => {
		onChange([...value.filter((item) => !SPARK_CODE_OPTIONS.has(item)), option]);
	};

	const displayValue = value.join(", ");

	const content = (
		<Popup onMouseDown={(event) => event.stopPropagation()}>
			<Search
				autoFocus
				value={query}
				placeholder="Search Deliverable Type"
				suffix={<SearchOutlined style={{ color: "#151515", fontSize: 20 }} />}
				onChange={(event) => setQuery(event.target.value)}
			/>
			<Options>
				{visibleGroups.map((group) => (
					<Group key={group.label}>
						<GroupLabel>{group.label}</GroupLabel>
						{group.options.map((option) => (
							<Option key={option}>
								{group.selection === "single" ? (
									<Radio checked={value.includes(option)} onChange={() => chooseSparkCode(option)} />
								) : (
									<Checkbox
										checked={value.includes(option)}
										onChange={() => toggleMultiple(option)}
									/>
								)}
								<span>{option}</span>
							</Option>
						))}
					</Group>
				))}
				{visibleGroups.length === 0 && <Empty>No deliverable types found</Empty>}
			</Options>
		</Popup>
	);

	return (
		<Popover
			open={open}
			trigger="click"
			placement="bottomLeft"
			arrow={false}
			content={content}
			styles={{ body: { padding: 0, background: "transparent", boxShadow: "none" } }}
			onOpenChange={(nextOpen) => {
				setOpen(nextOpen);
				if (!nextOpen) setQuery("");
			}}
		>
			<Trigger type="button" $open={open} aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open}>
				<TriggerValue $placeholder={!value.length} title={displayValue || undefined}>
					{displayValue || placeholder}
				</TriggerValue>
				<DownOutlined style={{ flex: "0 0 auto", fontSize: 12 }} />
			</Trigger>
		</Popover>
	);
};
